import type { GetStaticPaths, GetStaticPropsContext } from 'next'

import useRedirectV2UserToGradeOverview from '~/curriculum/hooks/useRedirectV2UserToGradeOverview'
import TheoryPage from '~/encyclopedia/pages/Theory.page'
import { AppLanguage } from '~/types/locale'
import { PreviewTopic, Topic, TopicOverview } from '~/types/theory'

import { TexLayout } from '~/shared/layouts/TexLayout'

import { getStaticPropsWrapper } from '~/utils/pageWrapper/staticPageWrapper'
import { htmlToPlainText } from '~/utils/htmlToPlainText'

import { DEFAULT_LAYOUT_TRANSLATION_MODULE_NAMES } from '~/shared/layouts/constants'

import { GetProps } from '~/utils/pageWrapper/types'

import { getTopicData } from '../utils/data-fetch/theory/theory.query'

interface TheoryPageWrapperProps {
  articleBody: string
  topic: Topic
  topicOverview: TopicOverview[]
  previewTopics: PreviewTopic[]
}

export default function TheoryPageWrapper({
  articleBody,
  topic,
  topicOverview,
  previewTopics,
}: TheoryPageWrapperProps): React.ReactElement {
  useRedirectV2UserToGradeOverview()

  return (
    <TheoryPage articleBody={articleBody} previewTopics={previewTopics} topic={topic} topicOverview={topicOverview} />
  )
}

TheoryPageWrapper.getLayout = TexLayout

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getProps: GetProps = () => async (context: GetStaticPropsContext<{ encyclopediaPaths: string[] }>) => {
  const { locale, params } = context
  const encyclopediaPaths = params?.encyclopediaPaths
  const validPaths = ['encyclopedia', 'geogebra', 'excel']
  if (!encyclopediaPaths || !encyclopediaPaths.some((part) => validPaths.includes(part))) {
    return {
      notFound: true,
    }
  }
  const topicData = await getTopicData({
    paths: encyclopediaPaths,
    language: locale as AppLanguage,
  })

  if (!topicData) {
    return { notFound: true }
  } else if ('destinationPath' in topicData) {
    return {
      redirect: {
        destination: topicData.destinationPath,
        statusCode: 308,
      },
    }
  }

  const { topic, topicOverview, previewTopics } = topicData

  const articleBody = 'htmlContent' in topic ? htmlToPlainText(topic.htmlContent.html) : ''
  return {
    props: {
      articleBody,
      path: `/${encyclopediaPaths.join('/')}`,
      topic,
      topicOverview,
      previewTopics,
      namespaces: [...DEFAULT_LAYOUT_TRANSLATION_MODULE_NAMES, topic.type as string, 'encyclopedia', 'gamification'],
    },
  }
}

export const getStaticProps = getStaticPropsWrapper(getProps)
